import { useState, useEffect } from 'react';
import { AuditInstance, EvidenceItem, LearningProgress, KeywordResearchProject, OnPageSeoProject, ContentPlanningProject, ContentPlanningPage } from './types';
import { MODULE_1_LECTURES_31_33 } from './modules/module1_lectures31_33';
import { LEARNING_MODULES } from './modules/learningModules';
import { StorageService } from './services/storageService';
import { LearningStorageService } from './services/learningStorageService';
import { KeywordResearchStorageService } from './services/keywordResearchStorageService';
import { OnPageStorageService } from './services/onPageStorageService';
import { ContentPlanningStorageService } from './services/contentPlanningStorageService';
import { EngineService } from './services/engine';

import { AppSidebar } from './components/Layout/AppSidebar';
import { AppHeader } from './components/Layout/AppHeader';
import { Sidebar } from './components/Layout/Sidebar';
import { AuditList } from './components/Dashboard/AuditList';
import { NewAuditModal } from './components/Dashboard/NewAuditModal';
import { StepWorkspace } from './components/GuidedStep/StepWorkspace';
import { FinalReportsCenter } from './components/Report/FinalReportsCenter';
import { LearningPathOverview } from './components/Learning/LearningPathOverview';
import { LessonReader } from './components/Learning/LessonReader';
import { KeywordProjectList } from './components/KeywordResearch/KeywordProjectList';
import { KeywordResearchWorkspace } from './components/KeywordResearch/KeywordResearchWorkspace';
import { OnPageProjectList } from './components/OnPageSeo/OnPageProjectList';
import { OnPageSeoWorkspace } from './components/OnPageSeo/OnPageSeoWorkspace';
import { ContentPlanningProjectList } from './components/ContentPlanning/ContentPlanningProjectList';
import { ContentPlanningWorkspace } from './components/ContentPlanning/ContentPlanningWorkspace';
import { LoginPage } from './components/Auth/LoginPage';

import './assets/main.css';

export function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return !!localStorage.getItem('seo_auth_session');
  });

  const [audits, setAudits] = useState<AuditInstance[]>([]);
  const [activeAudit, setActiveAudit] = useState<AuditInstance | null>(null);
  const [activeView, setActiveView] = useState<'learning' | 'dashboard' | 'workspace' | 'report' | 'lesson' | 'keyword_research' | 'keyword_workspace' | 'onpage' | 'onpage_workspace' | 'content_planning' | 'content_planning_workspace'>('learning');
  const [learningSubView, setLearningSubView] = useState<'overview' | 'path'>('overview');

  const handleLogout = () => {
    localStorage.removeItem('seo_auth_session');
    setIsAuthenticated(false);
  };

  // Content Planning state
  const [contentPlanningProjects, setContentPlanningProjects] = useState<ContentPlanningProject[]>([]);
  const [activeContentPlanningProject, setActiveContentPlanningProject] = useState<ContentPlanningProject | null>(null);
  
  const [currentStepId, setCurrentStepId] = useState<string>(
    MODULE_1_LECTURES_31_33.sections[0].steps[0].id
  );
  const [isNewAuditModalOpen, setIsNewAuditModalOpen] = useState(false);

  // Learning path state
  const [learningProgress, setLearningProgress] = useState<LearningProgress>(
    LearningStorageService.getProgress()
  );
  const [activeLessonId, setActiveLessonId] = useState<string>(
    LEARNING_MODULES[0].lessons[0].id
  );

  // Keyword Research state
  const [kwProjects, setKwProjects] = useState<KeywordResearchProject[]>([]);
  const [activeKwProject, setActiveKwProject] = useState<KeywordResearchProject | null>(null);

  // On-Page SEO state
  const [onPageProjects, setOnPageProjects] = useState<OnPageSeoProject[]>([]);
  const [activeOnPageProject, setActiveOnPageProject] = useState<OnPageSeoProject | null>(null);

  // Load all saved audits & keyword projects on mount
  useEffect(() => {
    async function loadData() {
      const list = await StorageService.getAllAudits();
      const migratedList = list.map(a => EngineService.migrateAudit(a, MODULE_1_LECTURES_31_33));
      setAudits(migratedList);

      const activeId = StorageService.getActiveAuditId();
      if (activeId) {
        const found = await StorageService.getAuditById(activeId);
        if (found) {
          const migratedActive = EngineService.migrateAudit(found, MODULE_1_LECTURES_31_33);
          setActiveAudit(migratedActive);
          if (migratedActive.currentStepId) {
            setCurrentStepId(migratedActive.currentStepId);
          }
        }
      } else if (migratedList.length > 0) {
        setActiveAudit(migratedList[0]);
        StorageService.setActiveAuditId(migratedList[0].id);
        if (migratedList[0].currentStepId) {
          setCurrentStepId(migratedList[0].currentStepId);
        }
      }

      let kwList = KeywordResearchStorageService.getAllProjects();
      let opList = OnPageStorageService.getAllProjects();
      let cpList = ContentPlanningStorageService.getAllProjects();

      for (const audit of migratedList) {
        let kwProj = kwList.find(
          p => p.linkedAuditId === audit.id || p.clientName.toLowerCase() === audit.clientName.toLowerCase() || p.websiteUrl === audit.websiteUrl
        );
        if (!kwProj) {
          kwProj = KeywordResearchStorageService.createProject(
            audit.clientName,
            audit.websiteUrl,
            'Scottsdale, AZ',
            audit.id
          );
          kwList.push(kwProj);
        }

        let opProj = opList.find(
          p => p.linkedAuditId === audit.id || p.clientName.toLowerCase() === audit.clientName.toLowerCase() || p.websiteUrl === audit.websiteUrl
        );
        if (!opProj) {
          opProj = OnPageStorageService.createProject(
            audit.clientName,
            audit.websiteUrl,
            kwProj.businessProfile?.primaryLocation || 'Sydney',
            audit.id,
            kwProj.id
          );
          opList.push(opProj);
        }

        let cpProj = cpList.find(
          p => p.linkedAuditId === audit.id || p.clientName.toLowerCase() === audit.clientName.toLowerCase() || p.websiteUrl === audit.websiteUrl
        );
        if (!cpProj) {
          cpProj = ContentPlanningStorageService.createProject(
            audit.clientName,
            audit.websiteUrl,
            kwProj.businessProfile?.primaryLocation || 'Global / Remote',
            'General Business',
            audit.id,
            kwProj.id
          );
          cpList.push(cpProj);
        }
      }

      setKwProjects(kwList);
      const activeKwId = KeywordResearchStorageService.getActiveProjectId();
      if (activeKwId) {
        const foundKw = kwList.find(p => p.id === activeKwId);
        if (foundKw) setActiveKwProject(foundKw);
      } else if (kwList.length > 0) {
        setActiveKwProject(kwList[0]);
        KeywordResearchStorageService.setActiveProjectId(kwList[0].id);
      }

      setOnPageProjects(opList);
      const activeOpId = OnPageStorageService.getActiveProjectId();
      if (activeOpId) {
        const foundOp = opList.find(p => p.id === activeOpId);
        if (foundOp) setActiveOnPageProject(foundOp);
      } else if (opList.length > 0) {
        setActiveOnPageProject(opList[0]);
        OnPageStorageService.setActiveProjectId(opList[0].id);
      }

      setContentPlanningProjects(cpList);
      const activeCpId = ContentPlanningStorageService.getActiveProjectId();
      if (activeCpId) {
        const foundCp = cpList.find(p => p.id === activeCpId);
        if (foundCp) setActiveContentPlanningProject(foundCp);
      } else if (cpList.length > 0) {
        setActiveContentPlanningProject(cpList[0]);
        ContentPlanningStorageService.setActiveProjectId(cpList[0].id);
      }
    }
    loadData();
  }, []);

  // Content Planning Project handlers
  const handleCreateContentPlanningProject = (
    clientName: string,
    websiteUrl: string,
    targetedLocation: string,
    category: string,
    linkedAuditId?: string,
    linkedKeywordId?: string,
    importedPages?: ContentPlanningPage[]
  ) => {
    const newProj = ContentPlanningStorageService.createProject(
      clientName,
      websiteUrl,
      targetedLocation,
      category,
      linkedAuditId,
      linkedKeywordId,
      importedPages
    );
    setContentPlanningProjects(prev => [newProj, ...prev]);
    setActiveContentPlanningProject(newProj);
    ContentPlanningStorageService.setActiveProjectId(newProj.id);
    setActiveView('content_planning_workspace');
  };

  const handleSelectContentPlanningProjectById = (id: string) => {
    const proj = contentPlanningProjects.find(p => p.id === id);
    if (proj) {
      setActiveContentPlanningProject(proj);
      ContentPlanningStorageService.setActiveProjectId(id);
      setActiveView('content_planning_workspace');
    }
  };

  const handleSaveContentPlanningProject = (proj: ContentPlanningProject) => {
    ContentPlanningStorageService.saveProject(proj);
    setActiveContentPlanningProject(proj);
    setContentPlanningProjects(prev => prev.map(p => (p.id === proj.id ? proj : p)));
  };

  const handleDeleteContentPlanningProject = (id: string) => {
    ContentPlanningStorageService.deleteProject(id);
    setContentPlanningProjects(prev => prev.filter(p => p.id !== id));
    if (activeContentPlanningProject?.id === id) {
      setActiveContentPlanningProject(null);
      setActiveView('content_planning');
    }
  };

  // Create a new audit instance
  const handleCreateAudit = async (clientName: string, websiteUrl: string) => {
    const newAudit = await StorageService.createAudit(clientName, websiteUrl);
    const migrated = EngineService.migrateAudit(newAudit, MODULE_1_LECTURES_31_33);
    setAudits(prev => [migrated, ...prev]);
    setActiveAudit(migrated);
    StorageService.setActiveAuditId(migrated.id);
    setCurrentStepId(MODULE_1_LECTURES_31_33.sections[0].steps[0].id);

    // Auto-sync project across all pipeline modules
    const newKw = KeywordResearchStorageService.createProject(clientName, websiteUrl, 'Scottsdale, AZ', migrated.id);
    const newOp = OnPageStorageService.createProject(clientName, websiteUrl, 'Sydney', migrated.id, newKw.id);
    const newCp = ContentPlanningStorageService.createProject(clientName, websiteUrl, 'Global / Remote', 'General Business', migrated.id, newKw.id);

    setKwProjects(prev => [newKw, ...prev]);
    setOnPageProjects(prev => [newOp, ...prev]);
    setContentPlanningProjects(prev => [newCp, ...prev]);

    setIsNewAuditModalOpen(false);
    setActiveView('workspace');
  };

  // Select an existing audit to edit/resume
  const handleSelectAuditById = (auditId: string) => {
    const found = audits.find(a => a.id === auditId);
    if (found) {
      const migrated = EngineService.migrateAudit(found, MODULE_1_LECTURES_31_33);
      setActiveAudit(migrated);
      StorageService.setActiveAuditId(migrated.id);
      if (migrated.currentStepId) {
        setCurrentStepId(migrated.currentStepId);
      } else {
        setCurrentStepId(MODULE_1_LECTURES_31_33.sections[0].steps[0].id);
      }
      setActiveView('workspace');
    }
  };

  // Delete an audit instance
  const handleDeleteAudit = async (auditId: string) => {
    await StorageService.deleteAudit(auditId);
    setAudits(prev => prev.filter(a => a.id !== auditId));
    if (activeAudit && activeAudit.id === auditId) {
      setActiveAudit(null);
      StorageService.setActiveAuditId('');
      setActiveView('dashboard');
    }
  };

  // Keyword Research Project handlers
  const handleCreateKwProject = (
    clientName: string,
    websiteUrl: string,
    location: string = 'Miami, FL',
    linkedAuditId?: string
  ) => {
    const newProj = KeywordResearchStorageService.createProject(clientName, websiteUrl, location, linkedAuditId);
    setKwProjects(prev => [newProj, ...prev]);
    setActiveKwProject(newProj);
    KeywordResearchStorageService.setActiveProjectId(newProj.id);

    // Sync to On-Page and Content Planning if not exists
    const existingOp = onPageProjects.find(p => p.clientName.toLowerCase() === clientName.toLowerCase() || p.websiteUrl === websiteUrl);
    if (!existingOp) {
      const newOp = OnPageStorageService.createProject(clientName, websiteUrl, location, linkedAuditId, newProj.id);
      setOnPageProjects(prev => [newOp, ...prev]);
    }

    const existingCp = contentPlanningProjects.find(p => p.clientName.toLowerCase() === clientName.toLowerCase() || p.websiteUrl === websiteUrl);
    if (!existingCp) {
      const newCp = ContentPlanningStorageService.createProject(clientName, websiteUrl, location, 'General Business', linkedAuditId, newProj.id);
      setContentPlanningProjects(prev => [newCp, ...prev]);
    }

    setActiveView('keyword_workspace');
  };

  const handleSelectKwProjectById = (id: string) => {
    const proj = kwProjects.find(p => p.id === id);
    if (proj) {
      setActiveKwProject(proj);
      KeywordResearchStorageService.setActiveProjectId(id);
      setActiveView('keyword_workspace');
    }
  };

  const handleSaveKwProject = (proj: KeywordResearchProject) => {
    KeywordResearchStorageService.saveProject(proj);
    setActiveKwProject(proj);
    setKwProjects(prev => prev.map(p => (p.id === proj.id ? proj : p)));
  };

  const handleDeleteKwProject = (id: string) => {
    KeywordResearchStorageService.deleteProject(id);
    setKwProjects(prev => prev.filter(p => p.id !== id));
    if (activeKwProject?.id === id) {
      setActiveKwProject(null);
      setActiveView('keyword_research');
    }
  };

  // On-Page SEO Project handlers
  const handleCreateOnPageProject = (
    clientName: string,
    websiteUrl: string,
    location: string,
    linkedAuditId?: string,
    linkedKeywordId?: string
  ) => {
    const newProj = OnPageStorageService.createProject(clientName, websiteUrl, location, linkedAuditId, linkedKeywordId);
    setOnPageProjects(prev => [newProj, ...prev]);
    setActiveOnPageProject(newProj);
    OnPageStorageService.setActiveProjectId(newProj.id);
    setActiveView('onpage_workspace');
  };

  const handleSelectOnPageProjectById = (id: string) => {
    const proj = onPageProjects.find(p => p.id === id);
    if (proj) {
      setActiveOnPageProject(proj);
      OnPageStorageService.setActiveProjectId(id);
      setActiveView('onpage_workspace');
    }
  };

  const handleSaveOnPageProject = (proj: OnPageSeoProject) => {
    OnPageStorageService.saveProject(proj);
    setActiveOnPageProject(proj);
    setOnPageProjects(prev => prev.map(p => (p.id === proj.id ? proj : p)));
  };

  const handleDeleteOnPageProject = (id: string) => {
    OnPageStorageService.deleteProject(id);
    setOnPageProjects(prev => prev.filter(p => p.id !== id));
    if (activeOnPageProject?.id === id) {
      setActiveOnPageProject(null);
      setActiveView('onpage');
    }
  };



  // Save answer to step in active audit
  const handleSaveStepAnswer = async (
    stepId: string,
    fieldValues: Record<string, any>,
    calculatedValues: Record<string, any>,
    evidence?: EvidenceItem,
    isComplete?: boolean
  ) => {
    if (!activeAudit) return;

    const currentAns = activeAudit.answers[stepId] || { completed: false, fieldValues: {} };
    const stepObj = MODULE_1_LECTURES_31_33.sections
      .flatMap(s => s.steps)
      .find(st => st.id === stepId);

    const calculated = stepObj
      ? EngineService.runCalculations(stepObj, fieldValues, activeAudit)
      : calculatedValues;

    const updatedStepAnswer = {
      ...currentAns,
      fieldValues,
      calculatedValues: calculated,
      evidence: evidence !== undefined ? evidence : currentAns.evidence,
      completed: isComplete !== undefined ? isComplete : true,
      completedAt: new Date().toISOString(),
    };

    let updatedAudit: AuditInstance = {
      ...activeAudit,
      answers: {
        ...activeAudit.answers,
        [stepId]: updatedStepAnswer,
      },
      updatedAt: new Date().toISOString(),
      currentStepId: stepId,
    };

    // Recalculate dependent step answers
    updatedAudit = EngineService.recalculateAllDependentAnswers(updatedAudit, MODULE_1_LECTURES_31_33);

    setActiveAudit(updatedAudit);
    await StorageService.saveAudit(updatedAudit);
    setAudits(prev => prev.map(a => (a.id === updatedAudit.id ? updatedAudit : a)));
  };

  // Handle step selection from sidebar / navigation
  const handleSelectStep = (stepId: string) => {
    setCurrentStepId(stepId);
    if (activeAudit) {
      const updated = { ...activeAudit, currentStepId: stepId };
      setActiveAudit(updated);
      StorageService.saveAudit(updated);
    }
  };

  // Learning path lesson completion
  const handleCompleteLesson = (lessonId: string, selectedAnswer?: string) => {
    const updated = LearningStorageService.markLessonComplete(lessonId, selectedAnswer);
    setLearningProgress(updated);
  };

  // Find active lesson object & adjacent lessons
  const allLessons = LEARNING_MODULES.flatMap(m => m.lessons);
  const currentLessonIndex = allLessons.findIndex(l => l.id === activeLessonId);
  const currentLesson = allLessons[currentLessonIndex] || allLessons[0];
  const prevLesson = currentLessonIndex > 0 ? allLessons[currentLessonIndex - 1] : null;
  const nextLesson = currentLessonIndex < allLessons.length - 1 ? allLessons[currentLessonIndex + 1] : null;

  // Find current step object for practical audit
  let currentStep = MODULE_1_LECTURES_31_33.sections[0].steps[0];
  for (const sec of MODULE_1_LECTURES_31_33.sections) {
    const found = sec.steps.find(st => st.id === currentStepId);
    if (found) {
      currentStep = found;
      break;
    }
  }

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Derive sidebar active view ID
  const getSidebarActiveView = (): string => {
    if (activeView === 'learning') {
      return learningSubView === 'path' ? 'learning_path' : 'learning_overview';
    }
    return activeView;
  };

  if (!isAuthenticated) {
    return <LoginPage onLoginSuccess={() => setIsAuthenticated(true)} />;
  }

  return (
    <div style={{ display: 'flex', width: '100%', maxWidth: '100vw', height: '100%', minHeight: '100dvh', overflow: 'hidden', background: '#050912', color: '#f8fafc' }}>
      
      {/* 1. PERSISTENT APP SIDEBAR NAVIGATION */}
      <AppSidebar
        activeView={getSidebarActiveView()}
        activeAudit={activeAudit}
        progress={learningProgress}
        isOpenMobile={isMobileMenuOpen}
        onCloseMobile={() => setIsMobileMenuOpen(false)}
        onNavigate={(view) => {
          setIsMobileMenuOpen(false);
          if (view === 'learning_overview') {
            setLearningSubView('overview');
            setActiveView('learning');
          } else if (view === 'workspace') {
            if (activeAudit) {
              setActiveView('workspace');
            } else {
              setActiveView('dashboard');
            }
          } else {
            setActiveView(view as any);
          }
        }}
      />

      {/* 2. MAIN APP CONTENT CONTAINER */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100dvh', minWidth: 0, overflow: 'hidden' }}>
        
        {/* Top App Header */}
        <AppHeader
          audit={activeAudit}
          activeView={activeView}
          progress={learningProgress}
          onNavigateView={(view) => {
            setIsMobileMenuOpen(false);
            setActiveView(view);
          }}
          onLogout={handleLogout}
          onToggleMobileMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        />

        {/* View Router Main Body */}
        <main style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {activeView === 'learning' && (
            <LearningPathOverview
              learningModules={LEARNING_MODULES}
              progress={learningProgress}
              activeLessonId={activeLessonId}
              initialSubView={learningSubView}
              onSelectLesson={(lessonId) => {
                setActiveLessonId(lessonId);
                setActiveView('lesson');
              }}
              onNavigatePracticalAudit={() => {
                if (activeAudit) {
                  setActiveView('workspace');
                } else {
                  setActiveView('dashboard');
                }
              }}
            />
          )}

          {activeView === 'lesson' && currentLesson && (
            <LessonReader
              lesson={currentLesson}
              isCompleted={learningProgress.completedLessonIds.includes(currentLesson.id)}
              onCompleteLesson={handleCompleteLesson}
              onNavigateLesson={(lessonId) => setActiveLessonId(lessonId)}
              onBackToOverview={() => setActiveView('learning')}
              prevLesson={prevLesson}
              nextLesson={nextLesson}
            />
          )}

          {activeView === 'keyword_research' && (
            <KeywordProjectList
              projects={kwProjects}
              audits={audits}
              onSelectProject={handleSelectKwProjectById}
              onCreateProject={handleCreateKwProject}
              onDeleteProject={handleDeleteKwProject}
            />
          )}

          {activeView === 'keyword_workspace' && (
            activeKwProject ? (
              <KeywordResearchWorkspace
                project={activeKwProject}
                audits={audits}
                onSaveProject={handleSaveKwProject}
                onBackToDashboard={() => setActiveView('keyword_research')}
              />
            ) : (
              <KeywordProjectList
                projects={kwProjects}
                audits={audits}
                onSelectProject={handleSelectKwProjectById}
                onCreateProject={handleCreateKwProject}
                onDeleteProject={handleDeleteKwProject}
              />
            )
          )}

          {activeView === 'onpage' && (
            <OnPageProjectList
              projects={onPageProjects}
              audits={audits}
              kwProjects={kwProjects}
              onSelectProject={handleSelectOnPageProjectById}
              onCreateProject={handleCreateOnPageProject}
              onDeleteProject={handleDeleteOnPageProject}
            />
          )}

          {activeView === 'onpage_workspace' && (
            activeOnPageProject ? (
              <OnPageSeoWorkspace
                project={activeOnPageProject}
                audits={audits}
                kwProjects={kwProjects}
                onSaveProject={handleSaveOnPageProject}
                onBackToDashboard={() => setActiveView('onpage')}
              />
            ) : (
              <OnPageProjectList
                projects={onPageProjects}
                audits={audits}
                kwProjects={kwProjects}
                onSelectProject={handleSelectOnPageProjectById}
                onCreateProject={handleCreateOnPageProject}
                onDeleteProject={handleDeleteOnPageProject}
              />
            )
          )}

          {activeView === 'content_planning' && (
            <ContentPlanningProjectList
              projects={contentPlanningProjects}
              audits={audits}
              kwProjects={kwProjects}
              onSelectProject={handleSelectContentPlanningProjectById}
              onCreateProject={handleCreateContentPlanningProject}
              onDeleteProject={handleDeleteContentPlanningProject}
            />
          )}

          {activeView === 'content_planning_workspace' && (
            activeContentPlanningProject ? (
              <ContentPlanningWorkspace
                project={activeContentPlanningProject}
                onBack={() => setActiveView('content_planning')}
                onSaveProject={handleSaveContentPlanningProject}
              />
            ) : (
              <ContentPlanningProjectList
                projects={contentPlanningProjects}
                audits={audits}
                kwProjects={kwProjects}
                onSelectProject={handleSelectContentPlanningProjectById}
                onCreateProject={handleCreateContentPlanningProject}
                onDeleteProject={handleDeleteContentPlanningProject}
              />
            )
          )}



          {activeView === 'dashboard' && (
            <AuditList
              audits={audits}
              module={MODULE_1_LECTURES_31_33}
              onSelectAudit={handleSelectAuditById}
              onNewAudit={() => setIsNewAuditModalOpen(true)}
              onDeleteAudit={handleDeleteAudit}
            />
          )}

          {activeView === 'workspace' && activeAudit && (
            <div className="main-layout">
              <Sidebar
                module={MODULE_1_LECTURES_31_33}
                audit={activeAudit}
                currentStepId={currentStepId}
                onSelectStep={handleSelectStep}
              />
              <main className="workspace">
                <StepWorkspace
                  key={currentStep.id}
                  step={currentStep}
                  audit={activeAudit}
                  module={MODULE_1_LECTURES_31_33}
                  onSaveAnswer={handleSaveStepAnswer}
                  onNavigateStep={handleSelectStep}
                  onNavigateReport={() => setActiveView('report')}
                />
              </main>
            </div>
          )}

          {activeView === 'report' && (
            <FinalReportsCenter
              audits={audits}
              kwProjects={kwProjects}
              onPageProjects={onPageProjects}
              module={MODULE_1_LECTURES_31_33}
              activeAudit={activeAudit}
              onSelectAuditReport={(audit) => {
                setActiveAudit(audit);
                StorageService.setActiveAuditId(audit.id);
              }}
              onSaveAudit={async (updatedAudit) => {
                setActiveAudit(updatedAudit);
                await StorageService.saveAudit(updatedAudit);
                setAudits(prev => prev.map(a => (a.id === updatedAudit.id ? updatedAudit : a)));
              }}
              onSaveKwProject={handleSaveKwProject}
              onSaveOnPageProject={handleSaveOnPageProject}
            />
          )}
        </main>
      </div>

      {/* New Audit Creation Modal */}
      <NewAuditModal
        isOpen={isNewAuditModalOpen}
        onClose={() => setIsNewAuditModalOpen(false)}
        onCreateAudit={handleCreateAudit}
      />
    </div>
  );
}

export default App;
